if (!customElements.get('product-fragrance-directory')) {
  customElements.define(
    'product-fragrance-directory',
    class ProductFragranceDirectory extends HTMLElement {
      connectedCallback() {
        this.chips = Array.from(this.querySelectorAll('[data-fragrance-chip]'));
        this.cards = Array.from(this.querySelectorAll('[data-fragrance-card]'));
        this.buttons = Array.from(this.querySelectorAll('[data-fragrance-add]'));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.successResetTimers = new WeakMap();
        this.variantPicker = document.querySelector('variant-selects');
        this.variantData = this.getVariantData();
        this.pickerValues = this.getPickerValues();

        if (!this.chips.length || !this.cards.length) return;

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.onChipClick = this.onChipClick.bind(this);
        this.onAddClick = this.onAddClick.bind(this);
        this.resolveFragranceButtons();

        this.chips.forEach((chip) => {
          chip.addEventListener('click', this.onChipClick);
        });

        this.buttons.forEach((button) => {
          button.addEventListener('click', this.onAddClick);
        });

        const hashTarget = window.location.hash ? window.location.hash.slice(1) : '';
        const initialTarget =
          hashTarget && this.cards.some((card) => card.id === hashTarget)
            ? hashTarget
            : this.chips.find((chip) => chip.classList.contains('is-active'))?.dataset.fragranceTarget || this.cards[0].id;

        this.setActiveState(initialTarget);
        this.setupObserver();
      }

      disconnectedCallback() {
        if (this.chips) {
          this.chips.forEach((chip) => {
            chip.removeEventListener('click', this.onChipClick);
          });
        }

        this.buttons.forEach((button) => {
          button.removeEventListener('click', this.onAddClick);
        });

        if (this.observer) {
          this.observer.disconnect();
        }
      }

      onChipClick(event) {
        event.preventDefault();

        const chip = event.currentTarget;
        const targetId = chip?.dataset?.fragranceTarget;
        const card = targetId ? document.getElementById(targetId) : null;

        if (!card) return;

        this.setActiveState(targetId);
        card.scrollIntoView({
          behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });

        if (window.history?.replaceState) {
          const nextUrl = new URL(window.location.href);
          nextUrl.hash = targetId;
          window.history.replaceState({}, '', nextUrl);
        }
      }

      async onAddClick(event) {
        const button = event.currentTarget;
        const labelNode = button?.querySelector('span');
        const defaultLabel = button?.dataset?.defaultLabel || labelNode?.textContent?.trim() || '';
        const addedLabel = button?.dataset?.addedLabel || 'Added';
        const variantId = await this.resolveVariantId(button);

        if (!variantId || button.classList.contains('loading')) return;

        const spinner = button.querySelector('.loading__spinner');
        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');
        if (spinner) spinner.classList.remove('hidden');

        try {
          const payload = {
            items: [
              {
                id: variantId,
                quantity: 1,
              },
            ],
          };

          if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            if (typeof this.cart.setActiveElement === 'function') {
              this.cart.setActiveElement(button);
            }
          }

          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });

          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Unable to add fragrance to cart.');
          }

          if (this.cart && typeof this.cart.renderContents === 'function' && data.sections) {
            if (!data.key && Array.isArray(data.items) && data.items[0] && data.items[0].key) {
              data.key = data.items[0].key;
            }
            this.cart.renderContents(data);
          } else {
            this.refreshCartBubble();
          }

          this.showAddedState(button, defaultLabel, addedLabel);
        } catch (error) {
          console.error(error);
        } finally {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      getVariantData() {
        const inlineVariantJson = this.querySelector('[data-fragrance-variants]');
        if (inlineVariantJson) {
          try {
            const parsed = JSON.parse(inlineVariantJson.textContent);
            if (Array.isArray(parsed)) return parsed;
          } catch (error) {
            console.error(error);
          }
        }

        const variantPicker = document.querySelector('variant-selects [type="application/json"]');
        if (variantPicker) {
          try {
            const parsed = JSON.parse(variantPicker.textContent);
            if (Array.isArray(parsed)) return parsed;
          } catch (error) {
            console.error(error);
          }
        }

        return [];
      }

      resolveFragranceButtons() {
        if (!this.buttons.length) return;

        this.buttons.forEach((button) => {
          const candidates = this.getButtonCandidates(button);
          const matchedPickerValue = this.findExplicitPickerValue(button) || this.findMatchingPickerValue(candidates);
          const matchedVariant = this.findMatchingVariant(button);
          const labelNode = button.querySelector('span');
          const defaultLabel = button.dataset.defaultLabel || labelNode?.textContent?.trim() || '';
          const soldOutLabel = button.dataset.soldOutLabel || defaultLabel;

          if (matchedPickerValue) {
            button.dataset.pickerValue = matchedPickerValue;
            button.removeAttribute('disabled');
            if (labelNode) labelNode.textContent = defaultLabel;
          }

          if (!matchedVariant) {
            return;
          }

          button.dataset.variantId = String(matchedVariant.id);

          if (matchedPickerValue) {
            button.removeAttribute('disabled');
            if (labelNode) labelNode.textContent = defaultLabel;
            return;
          }

          if (matchedVariant.available) {
            button.removeAttribute('disabled');
            if (labelNode) labelNode.textContent = defaultLabel;
          } else {
            button.setAttribute('disabled', 'disabled');
            if (labelNode) labelNode.textContent = soldOutLabel;
          }
        });
      }

      async resolveVariantId(button) {
        if (!button) return 0;

        const existingVariantId = Number(button.dataset.variantId || 0);
        const pickerValue =
          button.dataset.pickerValue ||
          this.findExplicitPickerValue(button) ||
          this.findMatchingPickerValue(this.getButtonCandidates(button));

        const mappedVariant = pickerValue ? this.findVariantByPickerValue(pickerValue) : null;
        if (mappedVariant?.id && mappedVariant.available) {
          button.dataset.variantId = String(mappedVariant.id);
          button.removeAttribute('disabled');
          return Number(mappedVariant.id);
        }

        if (existingVariantId && !button.hasAttribute('disabled')) {
          return existingVariantId;
        }

        if (!pickerValue || !this.selectPickerValue(pickerValue)) {
          return existingVariantId;
        }

        await this.waitForPickerSync();

        const mainVariantInput = this.getMainVariantInput();
        const resolvedVariantId = Number(mainVariantInput?.value || 0);
        const resolvedVariant = this.variantData.find((variant) => Number(variant?.id) === resolvedVariantId);

        if (!resolvedVariantId || (resolvedVariant && resolvedVariant.available === false)) {
          return existingVariantId;
        }

        button.dataset.variantId = String(resolvedVariantId);
        button.removeAttribute('disabled');

        return resolvedVariantId;
      }

      getMainVariantInput() {
        const sectionId = this.variantPicker?.dataset?.section;
        if (sectionId) {
          return document.querySelector(`#product-form-${sectionId} input[name="id"]`);
        }

        return document.querySelector('product-form form input[name="id"]');
      }

      getMainAddButton() {
        const sectionId = this.variantPicker?.dataset?.section;
        if (sectionId) {
          return document.getElementById(`ProductSubmitButton-${sectionId}`);
        }

        return document.querySelector('product-form [name="add"]');
      }

      selectPickerValue(pickerValue) {
        if (!this.variantPicker || !pickerValue) return false;

        const normalizedPickerValue = this.normalizeText(pickerValue);
        const radioInput = Array.from(this.variantPicker.querySelectorAll('input[type="radio"]')).find(
          (input) => this.normalizeText(input.value) === normalizedPickerValue
        );

        if (radioInput) {
          if (!radioInput.checked) {
            radioInput.checked = true;
            radioInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }

        const selectElement = Array.from(this.variantPicker.querySelectorAll('select')).find((select) =>
          Array.from(select.options).some(
            (option) =>
              this.normalizeText(option.value) === normalizedPickerValue ||
              this.normalizeText(option.textContent) === normalizedPickerValue
          )
        );

        if (!selectElement) return false;

        const matchingOption = Array.from(selectElement.options).find(
          (option) =>
            this.normalizeText(option.value) === normalizedPickerValue ||
            this.normalizeText(option.textContent) === normalizedPickerValue
        );

        if (!matchingOption) return false;

        selectElement.value = matchingOption.value;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      waitForPickerSync() {
        return new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(resolve);
          });
        });
      }

      findMatchingVariant(button) {
        const candidates = this.getButtonCandidates(button);
        if (!candidates.length) return null;

        const explicitPickerValue = this.findExplicitPickerValue(button);
        if (explicitPickerValue) {
          const explicitVariant = this.findVariantByPickerValue(explicitPickerValue);
          if (explicitVariant) return explicitVariant;
        }

        const matchedPickerValue = this.findMatchingPickerValue(candidates);
        if (matchedPickerValue) {
          const pickerVariant = this.findVariantByPickerValue(matchedPickerValue);
          if (pickerVariant) return pickerVariant;
        }

        let bestVariant = null;
        let bestScore = 0;
        let bestAvailableVariant = null;
        let bestAvailableScore = 0;

        this.variantData.forEach((variant) => {
          const score = this.scoreVariantMatch(variant, candidates);
          if (score > bestScore) {
            bestScore = score;
            bestVariant = variant;
          }
          if (variant?.available && score > bestAvailableScore) {
            bestAvailableScore = score;
            bestAvailableVariant = variant;
          }
        });

        if (bestAvailableVariant && bestAvailableScore >= 60) return bestAvailableVariant;
        return bestScore >= 60 ? bestVariant : null;
      }

      getPickerValues() {
        if (!this.variantPicker) return [];

        const rawValues = Array.from(
          this.variantPicker.querySelectorAll('input[type="radio"], option'),
          (element) => element.value || element.textContent || ''
        );

        return Array.from(
          new Set(
            rawValues
              .map((value) => value.trim())
              .filter(Boolean)
          )
        );
      }

      findExplicitPickerValue(button) {
        const fragranceAnchor = button?.dataset?.fragranceAnchor;
        if (!fragranceAnchor || !this.pickerValues.length) return null;

        const explicitValueMap = {
          citrus: ['سترس', 'سيترس', 'citrus'],
          'smoke-pine': ['الدخان والصنوبر', 'دخان وصنوبر', 'دخان و صنوبر', 'smoke pine', 'smoked pine'],
          'spiced-lavender': ['سبيسد لافندر', 'سبايسد لافندر', 'لافندر متبل', 'لافندر متبّل', 'spiced lavender'],
          'sweet-tobacco': ['سويت توباكو', 'تبغ حلو', 'sweet tobacco'],
          'espresso-timber': ['اسبريسو تمبر', 'اسبريسو تيمبر', 'إسبريسو تمبر', 'إسبريسو تيمبر', 'espresso timber'],
          'minted-fresh': ['مينتد فريش', 'انتعاش النعناع', 'minted fresh'],
        };

        const explicitCandidates = explicitValueMap[fragranceAnchor] || [];
        if (!explicitCandidates.length) return null;

        const normalizedPickerValues = this.pickerValues.map((value) => ({
          raw: value,
          normalized: this.normalizeText(value),
        }));

        for (const candidate of explicitCandidates) {
          const normalizedCandidate = this.normalizeText(candidate);
          const exactMatch = normalizedPickerValues.find((value) => value.normalized === normalizedCandidate);
          if (exactMatch) return exactMatch.raw;
        }

        for (const candidate of explicitCandidates) {
          const normalizedCandidate = this.normalizeText(candidate);
          const partialMatch = normalizedPickerValues.find(
            (value) =>
              value.normalized.includes(normalizedCandidate) ||
              normalizedCandidate.includes(value.normalized)
          );
          if (partialMatch) return partialMatch.raw;
        }

        return null;
      }

      findMatchingPickerValue(candidates) {
        if (!this.pickerValues.length) return null;

        let bestValue = null;
        let bestScore = 0;

        this.pickerValues.forEach((pickerValue) => {
          const score = this.scoreLabelMatch(this.normalizeText(pickerValue), candidates);
          if (score > bestScore) {
            bestScore = score;
            bestValue = pickerValue;
          }
        });

        return bestScore >= 60 ? bestValue : null;
      }

      findVariantByPickerValue(pickerValue) {
        const normalizedPickerValue = this.normalizeText(pickerValue);
        if (!normalizedPickerValue) return null;

        let bestVariant = null;
        let bestScore = 0;
        let bestAvailableVariant = null;
        let bestAvailableScore = 0;

        this.variantData.forEach((variant) => {
          const labels = this.getVariantLabels(variant);
          labels.forEach((label) => {
            if (!label) return;

            let score = 0;

            if (label === normalizedPickerValue) {
              score = 200;
            } else if (label.includes(normalizedPickerValue)) {
              score = 140 + normalizedPickerValue.length;
            } else if (normalizedPickerValue.includes(label) && label.length > 2) {
              score = 120 + label.length;
            } else {
              const pickerTokens = this.tokenize(normalizedPickerValue);
              const labelTokens = this.tokenize(label);
              const overlapCount = pickerTokens.filter((token) => labelTokens.includes(token)).length;

              if (overlapCount >= 2) {
                score = 110 + overlapCount * 4;
              }
            }

            if (score > bestScore) {
              bestScore = score;
              bestVariant = variant;
            }
            if (variant?.available && score > bestAvailableScore) {
              bestAvailableScore = score;
              bestAvailableVariant = variant;
            }
          });
        });

        if (bestAvailableVariant && bestAvailableScore >= 100) return bestAvailableVariant;
        return bestScore >= 100 ? bestVariant : null;
      }

      getButtonCandidates(button) {
        const rawCandidates = [
          button.dataset.fragranceName,
          button.dataset.fragranceDisplayName,
          button.dataset.fragranceAnchor,
          button.dataset.fragranceAliasEn,
          button.dataset.fragranceAliasAr,
          button.dataset.fragranceAliasEnAlt,
          button.dataset.fragranceAliasArAlt,
        ];

        return Array.from(
          new Set(
            rawCandidates
              .flatMap((candidate) => String(candidate || '').split('|'))
              .map((candidate) => this.normalizeText(candidate))
              .filter(Boolean)
          )
        );
      }

      getVariantLabels(variant) {
        const rawLabels = [variant?.title, variant?.public_title, variant?.name, variant?.option1, variant?.option2, variant?.option3];

        if (Array.isArray(variant?.options)) {
          rawLabels.push(...variant.options);
        }

        return Array.from(
          new Set(
            rawLabels
              .map((label) => this.normalizeText(label))
              .filter(Boolean)
          )
        );
      }

      scoreVariantMatch(variant, candidates) {
        const labels = this.getVariantLabels(variant);
        let bestScore = 0;

        labels.forEach((label) => {
          bestScore = Math.max(bestScore, this.scoreLabelMatch(label, candidates));
        });

        return bestScore;
      }

      scoreLabelMatch(label, candidates) {
        if (!label) return 0;

        let bestScore = 0;

        candidates.forEach((candidate) => {
          if (!candidate) return;

          const candidateTokens = this.tokenize(candidate);
          const labelTokens = this.tokenize(label);
          const overlapCount = candidateTokens.filter((token) => labelTokens.includes(token)).length;

          if (label === candidate) {
            bestScore = Math.max(bestScore, 120 + candidate.length);
            return;
          }

          if (candidateTokens.length > 1 && candidateTokens.every((token) => label.includes(token))) {
            bestScore = Math.max(bestScore, 95 + candidateTokens.length);
            return;
          }

          if (overlapCount >= 2) {
            bestScore = Math.max(bestScore, 88 + overlapCount * 4);
            return;
          }

          if (candidateTokens.length > 0 && overlapCount === candidateTokens.length && candidateTokens.length === labelTokens.length) {
            bestScore = Math.max(bestScore, 92 + overlapCount * 3);
            return;
          }

          if (label.includes(candidate)) {
            bestScore = Math.max(bestScore, 80 + candidate.length);
            return;
          }

          if (candidate.length > 4 && candidate.includes(label)) {
            bestScore = Math.max(bestScore, 65 + label.length);
          }
        });

        return bestScore;
      }

      tokenize(value) {
        const normalized = this.normalizeText(value);
        if (!normalized) return [];

        return Array.from(
          new Set(
            normalized
              .split(' ')
              .flatMap((token) => {
                if (!token) return [];

                const tokenVariants = [token];

                if (/^و[\u0600-\u06ff]+$/u.test(token) && token.length > 2) {
                  tokenVariants.push(token.slice(1));
                }

                if (token.startsWith('ال') && token.length > 3) {
                  tokenVariants.push(token.slice(2));
                }

                return tokenVariants.filter(Boolean);
              })
              .filter((token) => token && token !== 'و' && token !== 'ال')
          )
        );
      }

      normalizeText(value) {
        if (!value) return '';

        return String(value)
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[\u0640\u064B-\u065F\u0670\u06D6-\u06ED]/g, '')
          .replace(/[أإآٱ]/g, 'ا')
          .replace(/ؤ/g, 'و')
          .replace(/ئ/g, 'ي')
          .replace(/ى/g, 'ي')
          .replace(/ة/g, 'ه')
          .replace(/[_-]+/g, ' ')
          .replace(/[^\p{L}\p{N}\s]/gu, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
      }

      async refreshCartBubble() {
        const cartBubble = document.getElementById('cart-icon-bubble');
        if (!cartBubble) return;

        try {
          const response = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`);
          const markup = await response.text();
          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          const nextBubble = parsed.getElementById('cart-icon-bubble');
          if (nextBubble) {
            cartBubble.innerHTML = nextBubble.innerHTML;
          }
        } catch (error) {
          console.error(error);
        }
      }

      showAddedState(button, defaultLabel, addedLabel) {
        const labelNode = button?.querySelector('span');
        if (!labelNode) return;

        const existingTimer = this.successResetTimers.get(button);
        if (existingTimer) {
          window.clearTimeout(existingTimer);
        }

        labelNode.textContent = addedLabel;
        button.classList.add('is-added');

        const resetTimer = window.setTimeout(() => {
          labelNode.textContent = defaultLabel;
          button.classList.remove('is-added');
          this.successResetTimers.delete(button);
        }, 2200);

        this.successResetTimers.set(button, resetTimer);
      }

      setupObserver() {
        if (!('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            const visibleEntries = entries.filter((entry) => entry.isIntersecting);
            if (!visibleEntries.length) return;

            visibleEntries.sort((entryA, entryB) => entryB.intersectionRatio - entryA.intersectionRatio);
            this.setActiveState(visibleEntries[0].target.id);
          },
          {
            rootMargin: '-12% 0px -50% 0px',
            threshold: [0.25, 0.5, 0.75],
          }
        );

        this.cards.forEach((card) => {
          this.observer.observe(card);
        });
      }

      setActiveState(targetId) {
        if (!targetId) return;

        this.chips.forEach((chip) => {
          chip.classList.toggle('is-active', chip.dataset.fragranceTarget === targetId);
        });

        this.cards.forEach((card) => {
          card.classList.toggle('is-active', card.id === targetId);
        });
      }
    }
  );
}
